import React, { useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useOrderRedux, usePubSubEvents } from '@hooks'
import { usePubSub } from '@services'
import { Loader, OrderStatusList, OrderStatusTracker, OrderSummaryTotals } from '@components'
import { processOrderStatus, PUB_SUB_EVENT, ROUTES, CONNECTION_STATUS } from '@common'
import { useOrderEventContext } from '@context'

export default function OrderStatusPage() {
    const { orderNumber } = useParams()
    const navigate = useNavigate()
    const { isLoading, searchedOrder, fetchOrderByNumber } = useOrderRedux()
    const { setCurrentOrderId } = useOrderEventContext()
    const { connectionStatus, disconnect } = usePubSub()

    // Track previous connection status to detect reconnection
    const prevConnectionStatusRef = useRef(null)
    const didMountRef = useRef(false)
    const syncTimeoutRef = useRef(null)

    // listen to pubsub events
    usePubSubEvents({
        events: [PUB_SUB_EVENT.ORDER_PAYMENT_STATUS_UPDATE, PUB_SUB_EVENT.COMPLETED_DISH, PUB_SUB_EVENT.COMPLETED_ORDER],
        handlers: {
            [PUB_SUB_EVENT.COMPLETED_ORDER]: async order => {
                if (order.number === orderNumber) {
                    const res = await fetchOrderByNumber({ orderNumber, isForStatusPage: true })

                    if (res.meta.requestStatus === 'rejected') {
                        navigate(ROUTES.Order.search, { replace: true })
                    }
                }
            },
        },
    })

    const { lineitems, summary } = useMemo(() => processOrderStatus(searchedOrder), [searchedOrder])

    // const timerRef = useRef(null)

    // const startTimer = () => {
    //     // Clear any existing timer first
    //     if (timerRef.current) {
    //         clearInterval(timerRef.current)
    //     }
    //     // Start a new timer
    //     timerRef.current = setInterval(() => {
    //         fetchOrderByNumber(orderNumber)
    //     }, 60 * 1000)
    // }

    // const stopTimer = () => {
    //     if (timerRef.current) {
    //         clearInterval(timerRef.current)
    //         timerRef.current = null
    //     }
    // }

    const refreshOrder = useCallback(async () => {
        const res = await fetchOrderByNumber({ orderNumber, isForStatusPage: true })

        if (res.meta.requestStatus === 'rejected') {
            navigate(ROUTES.Order.search, { replace: true })
        }
    }, [orderNumber, navigate, fetchOrderByNumber])

    useEffect(() => {
        // Scroll to top when component mounts or orderNumber changes
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [orderNumber])

    useEffect(() => {
        setCurrentOrderId(searchedOrder?.orderId)

        return () => {
            setCurrentOrderId(null)
        }
    }, [searchedOrder, setCurrentOrderId])

    useEffect(() => {
        if (!searchedOrder || searchedOrder.number !== orderNumber) {
            refreshOrder()
        }

        // return () => {
        //     stopTimer()
        // }
    }, [orderNumber, navigate, fetchOrderByNumber, refreshOrder])

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshOrder()
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Intentionally empty - only run on mount, fetchOrderByNumber and orderNumber are stable

    // Sync data on WebSocket reconnect
    // Delay sync to ensure socket is fully ready and debounce multiple rapid reconnections
    const RECONNECT_SYNC_DELAY_MS = 200

    useEffect(() => {
        const prevStatus = prevConnectionStatusRef.current
        const currentStatus = connectionStatus

        // Skip first mount: only process reconnection after component has mounted at least once
        if (!didMountRef.current) {
            didMountRef.current = true

            if (currentStatus !== CONNECTION_STATUS.CONNECTED) {
                prevConnectionStatusRef.current = currentStatus
            }
            return
        }

        // Detect reconnection: transition from non-connected state to connected state
        const isReconnecting =
            prevStatus !== CONNECTION_STATUS.CONNECTED && // Was not connected before
            currentStatus === CONNECTION_STATUS.CONNECTED // Now connected

        if (isReconnecting) {
            // Clear any pending sync
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current)
            }

            syncTimeoutRef.current = setTimeout(() => {
                refreshOrder()
            }, RECONNECT_SYNC_DELAY_MS)
        }

        // Update previous status
        prevConnectionStatusRef.current = currentStatus

        // Cleanup timeout on unmount
        return () => {
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current)
            }
        }
    }, [connectionStatus, refreshOrder])

    return (
        <div>
            {isLoading && <Loader />}
            <div className="text-center mb-3">
                <p className="mb-2" style={{ color: '#888', fontSize: '1rem', marginBottom: '0.5rem' }}>
                    Order number
                </p>
                <h2 className="mb-3" style={{ color: '#28a745', fontSize: '2.25rem', fontWeight: 'bold' }}>
                    #{orderNumber}
                </h2>
            </div>
            <div className="d-flex align-items-end justify-content-between mb-3">
                <div
                    className="d-flex align-items-center justify-content-center"
                    onClick={() => navigate(ROUTES.root)}
                    style={{
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <i className="bx bx-left-arrow-alt bx-sm"></i>
                    <span>Back to Home</span>
                </div>
                <button
                    onClick={() => refreshOrder()}
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #d0d0d0',
                        borderRadius: '8px',
                        color: '#333',
                        fontSize: '20px',
                        width: '40px',
                        height: '40px',
                        padding: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                        e.target.style.backgroundColor = '#f8f9fa'
                        e.target.style.borderColor = '#999'
                    }}
                    onMouseLeave={e => {
                        e.target.style.backgroundColor = '#fff'
                        e.target.style.borderColor = '#d0d0d0'
                    }}
                >
                    <i className="bx bx-refresh"></i>
                </button>
                {/* <button
                    className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                    onClick={() => navigate(ROUTES.Order.receipt.replace(':orderNumber', orderNumber))}
                >
                    View Receipt
                    <i className="bx bx-right-arrow-alt bx-sm ms-2"></i>
                </button> */}
            </div>
            <OrderStatusTracker order={searchedOrder} />
            {searchedOrder && <OrderStatusList lineitems={lineitems} />}

            <div className="my-5 px-3">
                {summary && (
                    <OrderSummaryTotals
                        originalAmount={summary.originalAmount}
                        benefitDiscount={summary.benefitDiscount}
                        creditDiscount={summary.creditDiscount}
                        promoDiscount={summary.promoDiscount || 0}
                        finalAmount={summary.finalAmount}
                        baseFontSize="1rem"
                        totalFontSize="1.4rem"
                    />
                )}
            </div>
        </div>
    )
}
